import type { InputNumberRef } from 'rc-input-number';
import { useEffect, useRef, useState } from 'react';

import Icon, { MinusOutlined, PlusOutlined } from '@ant-design/icons';

import * as St from './ItemCountAdjustButton.styles';

import { handleDecimalNumberInput } from '~/core/utils';

export type ItemCountAdjustButtonProps = {
  quantity?: number;
  onDecrease?: () => void;
  onIncrease?: () => void;
  onUpdateValue?: (qty: number) => void;
  isDisabled?: boolean;
};

const ItemCountAdjustButton = ({
  quantity,
  onDecrease,
  onIncrease,
  onUpdateValue,
  isDisabled = false,
}: ItemCountAdjustButtonProps) => {
  const [productQuantity, setProductQuantity] = useState(quantity ?? 0);
  const canEdit = Boolean(onUpdateValue);
  const inputElement = useRef<InputNumberRef>(null);

  const updateQuantityValue = () => {
    onUpdateValue?.(productQuantity);
  };

  useEffect(() => {
    if (quantity) {
      setProductQuantity(quantity);
    }
  }, [quantity]);

  return quantity === undefined ? (
    <St.AddBtnAlone
      onClick={
        isDisabled
          ? undefined
          : (event) => {
              event.stopPropagation();
              onIncrease?.();
            }
      }
      disabled={isDisabled}
    >
      <Icon component={() => <PlusOutlined />} />
      <span>Add</span>
    </St.AddBtnAlone>
  ) : (
    <St.AdjustContainer>
      {onDecrease ? (
        <St.AdjustBtn
          className="start"
          data-can-edit={canEdit}
          onClick={(event) => {
            event.stopPropagation();
            onDecrease();
          }}
        >
          <Icon component={() => <MinusOutlined />} />
        </St.AdjustBtn>
      ) : (
        <div className="start" /> // Invisible placeholder
      )}

      {canEdit ? (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <St.InputNumberField
            className="center"
            min={0}
            ref={inputElement}
            value={quantity}
            onStep={(value) => onUpdateValue?.(Number(value))}
            onChange={(value) => {
              setProductQuantity(value ? Math.floor(Number(value)) : 0);
            }}
            onPressEnter={() => inputElement.current?.blur()}
            onBlur={updateQuantityValue}
            onKeyDown={handleDecimalNumberInput}
          />
        </div>
      ) : (
        <span className="center">{quantity}</span>
      )}

      {onIncrease && (
        <St.AdjustBtn
          className="end"
          data-can-edit={canEdit}
          onClick={(event) => {
            event.stopPropagation();
            onIncrease();
          }}
        >
          <Icon component={() => <PlusOutlined />} />
        </St.AdjustBtn>
      )}
    </St.AdjustContainer>
  );
};

export default ItemCountAdjustButton;
